import type { ChatTranscriptMessage, ToolCallData } from "../types/assistant.types"

type ThreadContentPart =
  | { readonly type: "text"; readonly text: string }
  | { readonly type: "data"; readonly name: string; readonly data: unknown }

export type ThreadMessageLike = {
  readonly role: "user" | "assistant"
  readonly content: ThreadContentPart[]
}

/** Convert a server-side ``ChatTranscriptMessage[]`` (the shape returned
 *  by ``IssueSerializer.conversation_transcript``) into the assistant-ui
 *  ``ThreadMessageLike`` shape that ``threadRuntime.reset`` expects.
 *
 *  Used by both the active-draft loader (``ThreadLoader``) and the
 *  history panel (``ThreadHistoryPanel``). They both need to push the
 *  same data parts (``ui_components``, ``attachments``, ``references``)
 *  so the registered DataUIs render historical user bubbles the same as
 *  live ones — keeping a single implementation here prevents drift, e.g.
 *  the historical-panel forgetting to rehydrate ``references`` and
 *  rendering reference chips only on freshly-sent turns. */
export function toThreadMessages(
  messages: ChatTranscriptMessage[],
): ThreadMessageLike[] {
  return messages.map((msg) => {
    const content: ThreadContentPart[] = []
    if (msg.content) {
      content.push({ type: "text", text: msg.content })
    }

    for (const comp of msg.ui_components) {
      if (comp && typeof comp === "object" && "component" in comp) {
        content.push({
          type: "data",
          name: (comp as { component: string }).component,
          data: comp,
        })
      }
    }

    for (const att of msg.attachments ?? []) {
      content.push({ type: "data", name: "attachment", data: att })
    }

    for (const ref of msg.references ?? []) {
      content.push({ type: "data", name: "chat_reference", data: ref })
    }

    for (const tc of msg.tool_calls ?? []) {
      const data: ToolCallData = {
        id: tc.id,
        name: tc.name,
        args: tc.args ?? {},
        status: tc.ok ? "done" : "error",
      }
      content.push({ type: "data", name: "tool_call", data })
    }

    return { role: msg.role as "user" | "assistant", content } as const
  })
}
